import React, { useState, useEffect } from 'react';
import styles from '../style/ThresholdModal.module.css';

function MeansModal({ isOpen, onClose, activeFile, sensorTranslations }) {
    const [meansData, setMeansData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen && activeFile) {
            const fetchMeans = async () => {
                setIsLoading(true);
                setError('');
                setMeansData([]);

                if (!activeFile?.fileObject) {
                    setError('No active file object is available for mean calculation.');
                    setIsLoading(false);
                    return;
                }

                const formData = new FormData();
                const uploadFilename =
                    activeFile?.name ||
                    activeFile?.fileObject?.name ||
                    'uploaded.csv';
                formData.append('file', activeFile.fileObject, uploadFilename);

                const apiBase = (process.env.REACT_APP_API_URL || '/api').replace(/\/+$/, '');
                const url = `${apiBase}/calculate-means`;

                try {
                    const response = await fetch(url, {
                        method: 'POST',
                        body: formData,
                    });
                    if (!response.ok) {
                        let message = 'Berechnung der Mittelwerte fehlgeschlagen';
                        try {
                            const errJson = await response.json();
                            if (errJson?.error) message = errJson.error;
                        } catch (_) {
                            message = response.statusText || message;
                        }
                        throw new Error(message);
                    }
                    const data = await response.json();

                    const sortedData = data.sort((a, b) => {
                        const numA = parseInt(a.id.replace('IW', ''), 10);
                        const numB = parseInt(b.id.replace('IW', ''), 10);
                        return numA - numB;
                    });
                    setMeansData(sortedData);

                } catch (err) {
                    setError(err.message || 'Berechnung der Mittelwerte fehlgeschlagen');
                } finally {
                    setIsLoading(false);
                }
            };
            fetchMeans();
        }
    }, [isOpen, activeFile]);

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={`${styles.modalContent} ${styles.meansModalContent}`} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>Berechnete Sensormittelwerte{activeFile?.name ? ` · ${activeFile.name}` : ''}</h2>
                    <button onClick={onClose} className={styles.closeButton} type="button">×</button>
                </div>
                <div className={styles.modalBody}>
                    {isLoading && <p>Berechne...</p>}
                    {error && <p className={styles.errorText}>{error}</p>}
                    {meansData.length > 0 && (
                        <table className={styles.meansTable}>
                            <thead>
                                <tr>
                                    <th>Sensor</th>
                                    <th>Mittelwert</th>
                                </tr>
                            </thead>
                            <tbody>
                                {meansData.map((item) => {
                                    const friendlyName = sensorTranslations?.[item.id] || item.name || item.id;
                                    return (
                                        <tr key={item.id}>
                                            <td className={styles.sensorCell}>
                                                <div className={styles.sensorLabelGroup}>
                                                    <span className={styles.sensorCode}>{item.id}</span>
                                                    <span className={styles.sensorReadable}>{friendlyName}</span>
                                                </div>
                                            </td>
                                            <td>{typeof item.mean === 'number' ? item.mean.toFixed(4) : item.mean}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className={styles.modalFooter}>
                    <button onClick={onClose} className={styles.cancelButton} type="button">Schließen</button>
                </div>
            </div>
        </div>
    );
}

export default MeansModal;
